/* eslint-disable class-methods-use-this */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Table, FormControl, Button, Modal, Badge, Form } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';
import moment from 'moment';
import debounce from '../../../../helpers/debounce';
import { graphql } from 'react-apollo';
import { NetworkStatus } from 'apollo-client';
import { compose } from 'redux';
import getLocationsQuery from '../../../../routes/site-admin/serviceFee/getLocations.graphql';
import getActiveDeliveryTypeQuery from '../../../../routes/site-admin/addCategory/getActiveDeliveryType.graphql';

// Style
import s from './BookingList.css';

// Helpers
import messages from '../../../../locale/messages';
import { bookingStatusLocale } from '../../../../helpers/bookingStatusLocale';
import { capitalizeFirstLetter } from '../../../../helpers/capitalizeFirst';
import Select from 'react-select';
import {
  bookingListBasePath,
  buildBookingListSearch,
  buildExportUrl,
  parseBookingFilters,
  toBookingQueryVariables,
  toList,
} from '../../../../helpers/bookingListFilters';
import { DELIVERY_TYPE } from '../../../../constants';
import { wita } from '../../../../helpers/witaTime';

// Components
import CustomPagination from '../../../CustomPagination';
import Link from '../../../Link';
import CurrencyConverter from '../../../CurrencyConverter';
import Loader from '../../../Common/Loader/Loader';
import ScamRiskBadge from '../../ScamRiskBadge/ScamRiskBadge';

import { FaCalendarAlt, FaDownload, FaMapPin, FaReceipt, FaFilter } from 'react-icons/fa';


function SelfPickupBadge() {
  return (
    <span className='text-info'>
      Self Pickup
    </span>
  );
}

// Rides come from RideBooking, whose ids collide with delivery Booking ids.
// orderId is NOT NULL on a delivery and null on a ride - see isRideRow.
const isRide = booking => Number(booking.deliveryType) === DELIVERY_TYPE.RIDE && !booking.orderId;

// Matches the multi-select styling used by the manage notifications form.
const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    borderRadius: '10px',
    border: state.isFocused ? '1.5px solid #E62529' : '1.5px solid #e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(230, 37, 41, 0.08)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#E62529' : '#d0d0d0',
    },
    minHeight: '44px',
    cursor: 'pointer',
    fontSize: '14px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#E62529' : state.isFocused ? 'rgba(230, 37, 41, 0.05)' : 'transparent',
    color: state.isSelected ? '#fff' : '#1c1c1e',
    cursor: 'pointer',
    fontSize: '14px',
    '&:active': {
      backgroundColor: '#E62529',
      color: '#fff',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#999',
  }),
  // The modal clips the menu without this.
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const DELIVERY_METHOD_OPTIONS = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'selfPickup', label: 'Self Pickup' },
];

function MultiSelectFilter({ label, value, options, placeholder, onChange }) {
  const selected = options.filter(option => toList(value).includes(option.value));

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <Select
        isMulti
        isSearchable
        value={selected}
        options={options}
        placeholder={placeholder}
        onChange={picked => onChange(picked ? picked.map(option => option.value) : [])}
        classNamePrefix='react-select'
        styles={SELECT_STYLES}
        menuPortalTarget={process.env.BROWSER ? document.body : null}
      />
    </Form.Group>
  );
}

const DELIVERY_TYPE_VARIANT = {
  [DELIVERY_TYPE.FOOD]: 'warning',
  [DELIVERY_TYPE.GROCERY]: 'success',
  [DELIVERY_TYPE.RIDE]: 'info',
};

function DeliveryTypeBadge({ booking }) {
  if (!booking.deliveryName) {
    return null;
  }

  return (
    <>
      <br />
      <Badge variant={DELIVERY_TYPE_VARIANT[booking.deliveryType] || 'secondary'}>
        {booking.deliveryName}
      </Badge>
    </>
  );
}


export class BookingList extends Component {
  static propTypes = {
    bookingDetails: PropTypes.object,
    filters: PropTypes.object,
    getLocationsData: PropTypes.shape({
      getLocations: PropTypes.array,
    }),
    getActiveDeliveryTypeData: PropTypes.shape({
      getActiveDeliveryType: PropTypes.shape({
        results: PropTypes.array,
      }),
    }),
  }

  static defaultProps = {
    filters: parseBookingFilters({}),
  }

  constructor(props) {
    super(props);

    // The route action reads the filters off the URL, so a remount caused by
    // going back, swiping back or refreshing starts from what the admin saw.
    this.state = {
      ...props.filters,
      showFilterModal: false
    };

    this.paginationData = this.paginationData.bind(this);
    this.handleKeywordSearch = this.handleKeywordSearch.bind(this);
    this.handleKeywordChange = this.handleKeywordChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.clearDateFilter = this.clearDateFilter.bind(this);
    this.openFilterModal = this.openFilterModal.bind(this);
    this.closeFilterModal = this.closeFilterModal.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.handleLocationChange = this.handleLocationChange.bind(this);
    this.handleDeliveryTypeChange = this.handleDeliveryTypeChange.bind(this);
    this.handleDeliveryMethodChange = this.handleDeliveryMethodChange.bind(this);

    this.debouncedSearch = debounce(this.handleKeywordSearch, 500);
  }

  openFilterModal() {
    this.setState({ showFilterModal: true });
  }

  closeFilterModal() {
    this.setState({ showFilterModal: false });
  }

  applyFilters() {
    this.runSearch(this.activeFilters({ currentPage: 1 }), { showFilterModal: false });
  }

  handleDateChange = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  }

  handleLocationChange = (location) => {
    this.setState({ location });
  }

  handleDeliveryTypeChange = (deliveryType) => {
    this.setState({ deliveryType });
  }

  handleDeliveryMethodChange = (deliveryMethod) => {
    this.setState({ deliveryMethod });
  }

  // The filters currently on screen, which is also what gets written to the URL.
  activeFilters(overrides = {}) {
    const { currentPage, keyword, startDate, endDate, location, deliveryType, deliveryMethod } = this.state;

    return {
      currentPage,
      keyword,
      startDate,
      endDate,
      location,
      deliveryType,
      deliveryMethod,
      ...overrides,
    };
  }

  // Mirrors the filters into the address bar so the next mount can restore them.
  // replaceState is called directly rather than through the history package
  // because the router listens to that package and would re-run the route
  // action, which remounts this list and steals focus from the search box.
  syncUrl(filters) {
    if (!process.env.BROWSER || !window.history.replaceState) return;

    const url = bookingListBasePath(this.props.requestType) + buildBookingListSearch(filters);

    // The existing state object carries the key the history package uses to
    // track scroll positions, so it is passed through untouched.
    window.history.replaceState(window.history.state, '', url);
  }

  // The spinner is driven by the query's network status, not by this promise:
  // under cache-and-network refetch resolves straight from the cache without
  // waiting for the server, so awaiting it would hide the spinner instantly.
  runSearch(filters, nextState = {}) {
    const { requestType, bookingDetails: { refetch } } = this.props;

    this.setState({ ...filters, ...nextState });
    this.syncUrl(filters);

    refetch(toBookingQueryVariables(filters, requestType))
      .catch(error => {
        console.error('Error during refetch:', error);
      });
  }

  clearDateFilter = () => {
    this.runSearch(
      this.activeFilters({
        currentPage: 1,
        startDate: '',
        endDate: '',
        location: [],
        deliveryType: [],
        deliveryMethod: [],
      }),
      { showFilterModal: false },
    );
  }

  handleKeywordSearch(keyword) {
    this.runSearch(this.activeFilters({ keyword: keyword || '', currentPage: 1 }));
  }

  handleKeywordChange(keyword) {
    // Update state immediately so input shows the value
    this.setState({ keyword });
    // Call debounced search
    this.debouncedSearch(keyword);
  }

  paginationData(currentPage) {
    this.runSearch(this.activeFilters({ currentPage }));
  }

  renderBookingStatus(status, notes, isDoorStepDelivery, deliveryPartnerDetails) {
    // ('pending', 'cancelledByUser', 'approved', 'declined', 'expired', 'cancelledByPartner', 'readyForDelivery', 'deliveryNotFound', 'deliveryApproved', 'deliveryDeclined', 'cancelledByDelivery', 'deliveryConfirmed', 'completed', 'failed', 'startDelivery'),

    const { locale } = this.props.intl;
    const classes = {
      pending: s.pending,
      completed: s.completed,
      declined: s.declined,
      expired: s.expired,
      approved: s.approved,
      readyForDelivery: s.approved,
      deliveryApproved: s.approved,
      deliveryConfirmed: s.approved,
      startDelivery: s.approved,
      deliveryNotFound: s.deliveryNotFound,
      deliveryDeclined: s.deliveryNotFound,
      cancelledByDelivery: s.deliveryNotFound,
      driverFound: s.approved,
      // RideBooking.tripStatus
      created: s.pending,
      scheduled: s.pending,
      started: s.approved,
      locationReached: s.approved,
      declineByRider: s.declined,
      cancelledByRider: s.declined,
      cancelledByDriver: s.deliveryNotFound,
    };

    if (isDoorStepDelivery === false && status === 'readyForDelivery') {
      status = 'readyForPickup';
    }

    if (isDoorStepDelivery === true && status === 'readyForDelivery' && deliveryPartnerDetails !== null) {
      status = 'driverFound'
    }

    return (
      <span className={cx(classes[status] || classes.declined, { 'd-inline-flex align-items-center': status === 'pending' })} title={notes}>
        {status === 'pending' && <span className="spinner-grow spinner-grow-sm flex-shrink-0" aria-hidden="true"></span>}
        <span className='flex-shrink-0'>{bookingStatusLocale(status, locale)}</span>
      </span>
    );
  }

  renderDeliveryType(booking) {
    // A ride is never self pickup: it either has a driver or is still unassigned.
    if (!booking.isDoorStepDelivery && !isRide(booking)) {
      return <SelfPickupBadge />;
    }

    if (!booking.deliveryPartnerDetails || !booking.deliveryPartnerDetails.firstName) {
      return <span className='text-muted'>None</span>;
    }

    return (
      <>
        {`${booking.deliveryPartnerDetails.firstName} ${booking.deliveryPartnerDetails.lastName}`}
        <br />
        <span className='text-muted'>
          {booking.deliveryPartnerDetails.phoneNumber}
        </span>
      </>
    );
  }

  // Fixed method to count orders for current page data
  getBookingCountForDate(date, results, dailyCounts) {
    const dailyCountItems = dailyCounts.find(item => item.date === date)?.count || 0;
    return dailyCountItems;
  }

  // Days are counted in WITA on both sides of the render. Formatting in local
  // time instead would put the boundary eight hours apart on the UTC server and
  // the WITA browser, and the extra separator row the server emitted would then
  // be hydrated over by the next booking row.
  renderDateSeparator(key) {
    const { bookingDetails: { getAllOrdersAdmin } } = this.props;
    const previousDate = key > 0 ? wita(getAllOrdersAdmin.results[key - 1].createdAt)?.format('YYYY-MM-DD') : null;
    const currentDate = wita(getAllOrdersAdmin.results[key].createdAt)?.format('YYYY-MM-DD');

    if (currentDate && previousDate !== currentDate) {
      // Count orders for this specific date from current results
      const ordersForThisDate = this.getBookingCountForDate(currentDate, getAllOrdersAdmin.results, getAllOrdersAdmin.dailyBookingCounts);

      return (
        <tr key={`separator-${key}`} className="date-separator">
          <td colSpan="8" className='d-block d-md-table-cell' style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#f0f0f0', width: '100%' }}>
            <FaCalendarAlt className='mr-1 text-muted' />
            <time className='mr-4'>{moment(currentDate).format('DD MMMM YYYY')}</time>
            <FaReceipt className='mr-1 text-muted' />
            <span className='text-danger'>
              {ordersForThisDate}
            </span> Orders
          </td>
        </tr>
      );
    }
    return null;
  }


  render() {
    const { formatMessage } = this.props.intl;
    const { bookingDetails, bookingDetails: { getAllOrdersAdmin, networkStatus }, requestType, getLocationsData, getActiveDeliveryTypeData } = this.props;
    const { currentPage, keyword, startDate, endDate, location, deliveryType, deliveryMethod, showFilterModal } = this.state;

    // setVariables and refetch mean the admin asked for a different set of rows;
    // loading is the background revalidation cache-and-network runs on mount,
    // which stays silent so returning to a list feels instant.
    const isSearching = networkStatus === NetworkStatus.setVariables
      || networkStatus === NetworkStatus.refetch;
    let filterCount = 0;
    if (toList(location).length) filterCount += 1;
    if (startDate || endDate) filterCount += 1;
    if (toList(deliveryType).length) filterCount += 1;
    if (toList(deliveryMethod).length) filterCount += 1;

    const locationOptions = ((getLocationsData && getLocationsData.getLocations) || [])
      .map(loc => ({ value: String(loc.id), label: loc.locationName }));

    const deliveryTypeOptions = ((getActiveDeliveryTypeData
      && getActiveDeliveryTypeData.getActiveDeliveryType
      && getActiveDeliveryTypeData.getActiveDeliveryType.results) || [])
      .map(dt => ({ value: String(dt.id), label: dt.deliveryName }));

    let exportType = 'bookings';
    let pageType = 'bookings/view';
    if (requestType === 'completed') {
      exportType = 'completed-bookings';
      pageType = 'completed-bookings/view';
    } else if (requestType === 'failed') {
      exportType = 'failed-bookings';
      pageType = 'failed-bookings/view';
    }

    // Carried into the detail page so its Go Back link can rebuild this view.
    const listSearch = buildBookingListSearch(this.activeFilters());

    return (
      <div className={cx(s.widthInner, s.tableSection, 'widthInnerNoBorder')}>
        <div className={s.controlsContainer}>
          <div className={s.filtersContainer}>
            <div className={s.searchInput}>
              <FormControl
                type='search'
                placeholder={formatMessage(messages.search)}
                value={keyword || ''}
                onChange={event => this.handleKeywordChange(event.target && event.target.value)}
              />
            </div>

            <div>
              <Button className={s.filterButton} variant="outline-dark" onClick={this.openFilterModal}>
                <FaFilter className='mr-1' />
                Filter {filterCount > 0 && <Badge variant="danger" className="ml-1">{filterCount}</Badge>}
              </Button>
            </div>
          </div>

          <div className={s.exportContainer}>
            {
              bookingDetails && getAllOrdersAdmin && getAllOrdersAdmin.count > 0 &&
              <a href={buildExportUrl(exportType, this.activeFilters())}
                className={'btn btn-outline-dark'}>
                <FormattedMessage {...messages.exportDataIntoCSV} />
                <FaDownload className='ml-2' />
              </a>
            }
          </div>
        </div>

        <div className={cx(s.tableCss, 'tableCss', 'tableSticky', 'NewAdminResponsiveTable')}>
          <Table className="table table-hover">
            <thead className='sticky-top'>
              <tr className='shadow-sm'>
                <th scope="col"><FormattedMessage {...messages.orderId} /></th>
                <th scope="col"><FormattedMessage {...messages.userLabel} /></th>
                <th scope="col"><FormattedMessage {...messages.shopLabel} /></th>
                <th scope="col"><FormattedMessage {...messages.driverLabel} /></th>
                <th scope="col"><FormattedMessage {...messages.orderStatus} /></th>
                <th scope="col"><FormattedMessage {...messages.bookingCreatedAt} /></th>
                <th scope="col"><FormattedMessage {...messages.orderAmount} /></th>
                <th scope="col"><FormattedMessage {...messages.action} /></th>
              </tr>
            </thead>
            <tbody>
              {
                isSearching && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                      <Loader type="circle" show={true} />
                    </td>
                  </tr>
                )
              }
              {
                !isSearching && bookingDetails
                && getAllOrdersAdmin
                && getAllOrdersAdmin.count > 0
                && getAllOrdersAdmin.results
                && getAllOrdersAdmin.results.length > 0
                && getAllOrdersAdmin.results.map((booking, key) => {
                  return (
                    // Rides have no orderId, and their id can equal a delivery
                    // id, so the type is part of the key. The key belongs on the
                    // fragment: without it React falls back to matching these
                    // rows by index, which breaks whenever a separator appears.
                    <React.Fragment key={`${booking.deliveryType}-${booking.id}`}>
                      {this.renderDateSeparator(key)}
                      <tr style={{ opacity: booking.deletedAt ? 0.5 : 1 }}>
                        <td data-label={formatMessage(messages.orderId)}>
                          {booking.deletedAt && (
                            <div>
                              (Deleted)
                            </div>
                          )}
                          {/* Rides get their own detail route - the delivery one
                              would open a different order under the same id. */}
                          {isRide(booking)
                            ? <Link to={`/siteadmin/bookings/view/ride/${booking.id}${listSearch}`}>
                              {`#${booking.id}`}
                            </Link>
                            : <Link to={`/siteadmin/${pageType}/${booking.id}${listSearch}`}>
                              {`#${booking.orderId}`}
                            </Link>
                          }
                          <DeliveryTypeBadge booking={booking} />
                        </td>
                        <td data-label={formatMessage(messages.userLabel)}>
                          {
                            booking.userDetails && booking.userName &&
                            <div>
                              {capitalizeFirstLetter(booking.userName)}<br />
                              <span className='text-muted'>
                                {booking.userDetails.phoneNumber}
                              </span>
                            </div>
                          }
                          {
                            (!booking.userDetails || !booking.userDetails.firstName) && <FormattedMessage {...messages.notFound} />
                          }
                        </td>
                        <td data-label={formatMessage(messages.shopLabel)}>
                          {
                            // A ride has no shop.
                            isRide(booking) && <span className='text-muted'>-</span>
                          }
                          {
                            !isRide(booking) && booking.shopDetails && booking.shopDetails.shopName &&
                            <div>
                              {capitalizeFirstLetter(booking.shopDetails.shopName)}
                              <br />
                              {booking.location && (
                                <>
                                  <span className='text-info'>
                                    <FaMapPin />
                                    {booking.location.locationName}
                                  </span>
                                  <br />
                                </>
                              )
                              }
                              <span className='text-muted'>
                                {booking.shopDetails.phoneNumber}
                              </span>
                            </div>
                          }
                          {
                            !isRide(booking) && (!booking.shopDetails || !booking.shopDetails.shopName) && <FormattedMessage {...messages.notFound} />
                          }
                        </td>
                        <td data-label={formatMessage(messages.driverLabel)}>
                          {this.renderDeliveryType(booking)}
                        </td>
                        <td data-label={formatMessage(messages.orderStatus)}>
                          {this.renderBookingStatus(booking.bookingStatus, booking.notes, booking.isDoorStepDelivery, booking?.deliveryPartnerDetails)}
                          {booking.scamRisk && (
                            <>
                              <br />
                              <span className="d-inline-block mt-2">
                                <ScamRiskBadge risk={booking.scamRisk} />
                              </span>
                            </>
                          )}
                        </td>
                        <td data-label={formatMessage(messages.bookingCreatedAt)}>
                          <time title={moment(new Date(booking.createdAt).toISOString()).fromNow()}>
                            {wita(booking.createdAt)?.format('DD-MM-YYYY HH:mm')}
                          </time>
                        </td>
                        <td data-label={formatMessage(messages.orderAmount)}>
                          {booking.isSpecialDelivery ? <s>
                            <CurrencyConverter
                              from={booking.currency}
                              amount={booking.isSpecialDelivery ? booking.specialDeliveryTotalFare + booking.specialDeliveryFare : booking.userPayableFare}
                              className={cx({ "text-muted": booking.isSpecialDelivery })}
                            />
                          </s> :
                            <CurrencyConverter
                              from={booking.currency}
                              amount={booking.userPayableFare}
                            />
                          }
                          {Boolean(booking.isSpecialDelivery) && <>
                            <br />
                            <CurrencyConverter
                              from={booking.currency}
                              amount={booking.specialDeliveryTotalFare}
                            />
                          </>
                          }
                        </td>
                        <td data-label={formatMessage(messages.action)}>
                          <Link to={isRide(booking)
                            ? `/siteadmin/bookings/view/ride/${booking.id}${listSearch}`
                            : `/siteadmin/${pageType}/${booking.id}${listSearch}`}>
                            <FormattedMessage {...messages.view} />
                          </Link>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              }
              {
                !isSearching && (!getAllOrdersAdmin || getAllOrdersAdmin.count === 0 || !getAllOrdersAdmin.results || getAllOrdersAdmin.results.length === 0) &&
                <tr>
                  <td colSpan="8" className={s.noRecords}>
                    <FormattedMessage {...messages.noResult} />
                  </td>
                </tr>
              }
            </tbody>
          </Table>
        </div>
        {
          getAllOrdersAdmin && getAllOrdersAdmin.count > 0 && <div className={cx(s.space5, s.spaceTop5)}>
            <CustomPagination
              total={getAllOrdersAdmin.count}
              currentPage={currentPage}
              defaultCurrent={1}
              defaultPageSize={50}
              change={this.paginationData}
              paginationLabel={formatMessage(messages.ordersLabel)}
            />
          </div>
        }

        <Modal show={showFilterModal} onHide={this.closeFilterModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Filter Orders</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <MultiSelectFilter
              label={formatMessage(messages.location)}
              value={location}
              options={locationOptions}
              placeholder={formatMessage(messages.allLocations)}
              onChange={this.handleLocationChange}
            />
            <MultiSelectFilter
              label='Delivery Type'
              value={deliveryType}
              options={deliveryTypeOptions}
              placeholder='All Types'
              onChange={this.handleDeliveryTypeChange}
            />
            <MultiSelectFilter
              label='Delivery Method'
              value={deliveryMethod}
              options={DELIVERY_METHOD_OPTIONS}
              placeholder='All Methods'
              onChange={this.handleDeliveryMethodChange}
            />
            <Form.Group>
              <Form.Label>{formatMessage(messages.dateRange)}</Form.Label>
              <div className={s.dateFilters}>
                <FormControl
                  type="date"
                  name="startDate"
                  value={startDate || ''}
                  onChange={this.handleDateChange}
                  className={s.dateInput}
                />
                <span className={s.dateRangeSeparator}>to</span>
                <FormControl
                  type="date"
                  name="endDate"
                  value={endDate || ''}
                  onChange={this.handleDateChange}
                  className={s.dateInput}
                />
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.closeFilterModal}>
              Close
            </Button>
            <Button variant="danger" onClick={this.clearDateFilter}>
              Reset
            </Button>
            <Button variant="primary" onClick={this.applyFilters}>
              Apply Filters
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default compose(
  injectIntl,
  withStyles(s),
  graphql(getLocationsQuery, {
    name: 'getLocationsData',
  }),
  graphql(getActiveDeliveryTypeQuery, {
    name: 'getActiveDeliveryTypeData',
  }),
)(BookingList);
